import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Tile, SkeletonText } from '@carbon/react';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { HiddenColumns } from 'components/table/hidden-columns';
import {
  TableHeader,
  TableControls,
  formatExportData,
} from 'components/allocation-manager/table';
import { isSubtotalRow } from 'components/allocation-manager/table/table-utils';
import styles from './shared-data-table.module.scss';
import { ExportToCsvButton } from './shared-data-table-utils';
import {
  objectValueSorting,
  columnFilterFn,
  globalFilterFn,
} from './table-filter-utils';
import { isObject, has, isNil, isEmpty, isEqual, map } from 'lodash-es';
import { TableBody } from '../table/table-body';
import classNames from 'classnames';

export interface ColumnDef {
  key: string;
  header: string;
  meta?: {
    filterVariant?: 'text' | 'range' | 'dropdown';
    [key: string]: any;
  };
}
export interface TableHeader extends ColumnDef {}

export interface FormattedCell {
  content: React.ReactNode;
  onClick?: () => void;
}

export interface SharedDataTableProps<T> {
  data: T[];
  headers: ColumnDef[];
  formatCellValue: (
    key: string,
    value: any,
    row: any
  ) => string | FormattedCell | React.ReactNode | null;
  filename: string;
  tileClassName?: string;
  allocationMethod?: 'acreage' | 'lateral_length';
  loading?: boolean;
  initialColumnFilters?: { id: string; value: string }[] | null;
  onRowClick?: (row: T) => void;
  selectedRowId?: string | null;
  warningContent?: React.ReactNode;
  onFilteredDataChange?: (filteredData: T[]) => void;
  currentThreshold: number;
  onThresholdChange: (threshold: number) => void;
  onThresholdReset: () => void;
  getSubtotalRow?: (rows: any[]) => Record<string, React.ReactNode>;
  participationFactorThreshold: number | null;
  onParticipationFactorThresholdChange: (threshold: number | null) => void;
}

export function isFormattedCell(value: any): value is FormattedCell {
  return isObject(value) && has(value, 'content');
}

const APPROX_CHAR_WIDTH = 8;
const FILTER_BY_WIDTH = 80;
const EXTRA_PADDING = 40;

export function SharedDataTable<T extends Record<string, any>>({
  data,
  headers,
  formatCellValue,
  filename,
  tileClassName = styles.tileTab,
  loading,
  initialColumnFilters = [],
  onRowClick,
  selectedRowId,
  warningContent,
  onFilteredDataChange,
  currentThreshold,
  onThresholdChange,
  onThresholdReset,
  getSubtotalRow,
  participationFactorThreshold,
  onParticipationFactorThresholdChange,
}: SharedDataTableProps<T>) {
  const tableId = `${filename}-table`;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialColumnFilters || []
  );

  const { isColumnFilterActive, setIsColumnFilterActive } =
    useColumnFilter(tableId);

  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);

  const columnHelper = createColumnHelper<T>();

  const columns = useMemo(() => {
    return headers.map((header) => {
      const headerWidth = header.header.length * APPROX_CHAR_WIDTH;
      const totalWidth = headerWidth + FILTER_BY_WIDTH + EXTRA_PADDING;

      return columnHelper.accessor((row) => row[header.key], {
        id: header.key,
        header: header.header,
        size: totalWidth,
        minSize: Math.max(totalWidth, 175),
        cell: (info) => {
          const value = info.getValue();
          const formattedValue = formatCellValue(
            header.key,
            value,
            info.row.original
          );

          if (isFormattedCell(formattedValue)) {
            return formattedValue.onClick ? (
              <div
                onClick={formattedValue.onClick}
                style={{ cursor: 'pointer' }}
                role="button"
              >
                {formattedValue.content}
              </div>
            ) : (
              formattedValue.content
            );
          }

          return formattedValue;
        },
        enableColumnFilter: true,
        enableResizing: true,
        sortingFn: objectValueSorting,
        filterFn: columnFilterFn,
        meta: {
          filterVariant: header.meta?.filterVariant || 'text',
          ...(header.meta || {}),
        },
      });
    });
  }, [headers, columnHelper, formatCellValue]);

  const initialColumnOrder = useMemo(
    () => columns.map((column) => column.id as string),
    [columns]
  );

  const { columnOrder, setColumnOrder } = useColumnState({
    tableId,
    initialColumnOrder,
    initialColumnPinning: { left: [] },
  });

  useEffect(() => {
    setIsColumnFilterActive(true);
    setColumnFilters(initialColumnFilters || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColumnFilters]);

  const resetCustomizations = useCallback(() => {
    setColumnFilters([]);
    setIsColumnFilterActive(false);
  }, [setColumnFilters, setIsColumnFilterActive]);

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {},
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      columnFilters,
      columnSizing,
      columnOrder,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      columnOrder: initialColumnOrder,
    },
    meta: {
      reset: {
        resetCustomizations,
      },
    },
    enableColumnResizing: true,
    globalFilterFn: globalFilterFn,
    enableFilters: true,
  });

  const { rows } = table.getRowModel();

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 20,
  });

  const exportData = useMemo(() => {
    const processedData = table.getFilteredRowModel().rows;
    if (isEmpty(data) || isEmpty(processedData)) return [];
    return formatExportData(table, headers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, headers, data, columnFilters]);

  const filteredDataRef = useRef<any[]>([]);

  useEffect(() => {
    if (isNil(onFilteredDataChange) || isNil(table)) return;

    const rows = table.getFilteredRowModel().rows;
    const filtered = rows
      .filter((row) => !isSubtotalRow(row))
      .map((row) => row.original);

    if (!isEqual(map(filtered, 'id'), map(filteredDataRef.current, 'id'))) {
      filteredDataRef.current = filtered;
      onFilteredDataChange(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, globalFilter, data.length, onFilteredDataChange]);

  if (loading) {
    return (
      <Tile className={tileClassName}>
        <div
          className={classNames(styles.skeletonContainer, 'shared-data-table')}
        >
          <SkeletonText heading width="40%" />
          <div style={{ height: '20px' }} />
          <SkeletonText paragraph lineCount={15} />
        </div>
      </Tile>
    );
  }

  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  return (
    <Tile className={tileClassName}>
      <div className={styles.dataTableWrapper}>
        <TableControls
          table={table}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          isColumnFilterActive={isColumnFilterActive}
          setIsColumnFilterActive={setIsColumnFilterActive}
          exportData={exportData}
          headers={headers}
          filename={filename}
          ExportButton={ExportToCsvButton}
          currentThreshold={currentThreshold}
          onThresholdChange={onThresholdChange}
          onThresholdReset={onThresholdReset}
          participationFactorThreshold={participationFactorThreshold}
          onParticipationFactorThresholdChange={
            onParticipationFactorThresholdChange
          }
        />
        {warningContent && (
          <div className={styles.warningContent}>{warningContent}</div>
        )}
        {isColumnVisible ? (
          <div className={styles.tableContainer}>
            <div ref={parentRef} className={styles.stackTableContainer}>
              <table className={styles.stackTable}>
                <TableHeader
                  headerGroups={table.getHeaderGroups()}
                  isColumnFilterActive={isColumnFilterActive}
                />
                <TableBody
                  table={table}
                  virtualizer={virtualizer}
                  onRowClick={onRowClick}
                  selectedRowId={selectedRowId}
                  getSubtotalRow={getSubtotalRow}
                />
              </table>
            </div>
          </div>
        ) : (
          <HiddenColumns className={styles.hiddenColumn} />
        )}
        {isColumnVisible && (
          <div className={styles.totalRowCount}>
            <label className={styles.totalRows}>
              {table.getRowModel().rows.length > 0
                ? table.getRowModel().rows.filter((row) => !isSubtotalRow(row))
                    .length
                : 0}
            </label>
            {table.getRowModel().rows.length > 0 &&
            table.getRowModel().rows.filter((row) => !isSubtotalRow(row))
              .length === 1
              ? 'row'
              : 'rows'}
          </div>
        )}
      </div>
    </Tile>
  );
}
